import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../ui/Logo';
import Icon from '../ui/Icon';
import { uiActions, authActions } from '../../store';
import { TOOL_CATEGORIES, getToolsByCategory } from '../../data/tools';

const Navbar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { darkMode, mobileMenuOpen } = useSelector(s => s.ui);
  const { isAuthenticated, user, freeDocsUsed, freeDocsLimit } = useSelector(s => s.auth);
  const [scrolled, setScrolled] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setToolsOpen(false);
    dispatch(uiActions.setMobileMenuOpen(false));
  }, [location, dispatch]);

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Tools', to: '/tools', hasMega: true },
    { label: 'Pricing', to: '/pricing' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass-strong shadow-md py-2' : 'py-4'
        }`}
        style={{ background: scrolled ? 'var(--glass-bg)' : 'transparent' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <Logo size="md" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(link => (
                <div key={link.label} className="relative"
                  onMouseEnter={() => link.hasMega && setToolsOpen(true)}
                  onMouseLeave={() => link.hasMega && setToolsOpen(false)}
                >
                  <Link
                    to={link.to}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                      location.pathname === link.to
                        ? 'text-brand-600 bg-brand-50'
                        : 'hover:bg-black/5'
                    }`}
                    style={{ color: location.pathname === link.to ? '#3374ff' : 'var(--text-primary)' }}
                  >
                    {link.label}
                    {link.hasMega && <Icon name="ChevronDown" size={14} />}
                  </Link>

                  {/* Mega Menu */}
                  {link.hasMega && (
                    <AnimatePresence>
                      {toolsOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                          style={{ width: '720px' }}
                        >
                          <div className="glass-strong rounded-2xl shadow-xl p-6 border" style={{ borderColor: 'var(--border-light)' }}>
                            <div className="grid grid-cols-3 gap-4">
                              {Object.values(TOOL_CATEGORIES).slice(0, 6).map(cat => (
                                <div key={cat.id}>
                                  <h4 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5"
                                    style={{ color: cat.color }}>
                                    <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                                    {cat.label}
                                  </h4>
                                  <div className="space-y-0.5">
                                    {getToolsByCategory(cat.id).slice(0, 4).map(tool => (
                                      <Link
                                        key={tool.id}
                                        to={`/tool/${tool.id}`}
                                        className="block px-2 py-1.5 rounded-lg text-sm hover:bg-black/5 transition-colors"
                                        style={{ color: 'var(--text-secondary)' }}
                                      >
                                        {tool.name}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-4 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
                              <Link to="/tools" className="text-sm font-semibold flex items-center gap-1" style={{ color: '#3374ff' }}>
                                View All Tools <Icon name="ArrowRight" size={14} />
                              </Link>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>40+ tools available</span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Usage Badge */}
              {!isAuthenticated && (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                  <div className="flex gap-0.5">
                    {[...Array(freeDocsLimit)].map((_, i) => (
                      <div key={i} className="w-1.5 h-4 rounded-full transition-colors"
                        style={{ background: i < freeDocsUsed ? '#3374ff' : 'var(--border-light)' }} />
                    ))}
                  </div>
                  <span>{freeDocsLimit - freeDocsUsed} free left</span>
                </div>
              )}

              {/* Dark Mode Toggle */}
              <button
                onClick={() => dispatch(uiActions.toggleDarkMode())}
                className="p-2 rounded-lg transition-colors hover:bg-black/5"
              >
                <Icon name={darkMode ? 'Sun' : 'Moon'} size={18} />
              </button>

              {/* Command Palette */}
              <button
                onClick={() => dispatch(uiActions.setCommandPalette(true))}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border-light)' }}
              >
                <Icon name="Search" size={14} />
                <span>Search</span>
                <kbd className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-tertiary)' }}>⌘K</kbd>
              </button>

              {/* Auth Button */}
              {isAuthenticated ? (
                <Link to="/dashboard" className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                    {user?.name?.[0] || 'U'}
                  </div>
                </Link>
              ) : (
                <button onClick={() => dispatch(authActions.setShowAuthModal(true))}
                  className="btn-primary text-sm !px-5 !py-2">
                  Sign In
                </button>
              )}

              {/* Mobile Menu */}
              <button onClick={() => dispatch(uiActions.setMobileMenuOpen(!mobileMenuOpen))}
                className="md:hidden p-2 rounded-lg hover:bg-black/5">
                <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={22} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => dispatch(uiActions.setMobileMenuOpen(false))} />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute right-0 top-0 bottom-0 w-80 p-6 pt-20"
              style={{ background: 'var(--bg-primary)' }}
            >
              <div className="space-y-2">
                {navLinks.map(link => (
                  <Link key={link.label} to={link.to}
                    className="block px-4 py-3 rounded-xl text-lg font-medium transition-colors hover:bg-black/5"
                    style={{ color: 'var(--text-primary)' }}>
                    {link.label}
                  </Link>
                ))}
                <Link to="/dashboard"
                  className="block px-4 py-3 rounded-xl text-lg font-medium transition-colors hover:bg-black/5"
                  style={{ color: 'var(--text-primary)' }}>
                  Dashboard
                </Link>
              </div>
              <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-light)' }}>
                {!isAuthenticated && (
                  <button onClick={() => { dispatch(authActions.setShowAuthModal(true)); dispatch(uiActions.setMobileMenuOpen(false)); }}
                    className="btn-primary w-full">
                    Sign In Free
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
